import React, { useState } from "react";
import { Row, Col, Upload, Input, Spin } from "antd";
import DatePicker from "components/shared/DatePicker";
import { useTranslation } from "react-i18next";
import { CloseOutlined } from "@ant-design/icons";

import Modal from "components/shared/Modal";

import { dateFormatter, dateMomentFormatter, dateIsLater } from "utils/dateUtils";
import { validateFiles, processFileValidationMessages, fileSizeIsValid } from "utils/fileUtils";
import { requiredField } from "utils/formUtils";
import { removeTitles } from "utils/helpers";
import settings from "config/config";

const { Dragger } = Upload;
const { TextArea } = Input;

const Prolong = (props) => {
  const { t } = useTranslation();

  const defaultIncreaseLimitData = {
    validTo: 0,
    explanation: "",
    files: [],
  };

  const [increaseLimitData, setIncreaseLimitData] = useState(defaultIncreaseLimitData);

  const [invoiceFiles, setInvoiceFiles] = useState([]);

  const [loading, setLoading] = useState(false);
  const [fileUploadLoading, setFileUploadLoading] = useState(false);
  const [showValidationError, setShowValidationError] = useState(false);

  const onChange = async (info, callback) => {
    const { status } = info.file;

    if (status !== "uploading") {
      console.log(info.file, info.fileList);
    }
    if (status === "done") {
      console.log(`${info.file.name} file uploaded successfully.`);
      callback(info);
    } else if (status === "error") {
      console.log(`${info.file.name} file upload failed.`);
    }
  };

  const onInvoiceFilesChange = async (info) => {
    const files = [...processFileValidationMessages(info, t)];

    setInvoiceFiles(validateFiles(files));

    setFileUploadLoading(true);

    onChange(info, async function (response) {
      if (response.file) {
        const newIncreaseLimitData = { ...increaseLimitData };

        newIncreaseLimitData.files = [...increaseLimitData.files, response.file.response];

        setIncreaseLimitData(newIncreaseLimitData);
      }
    });

    setFileUploadLoading(false);
    removeTitles();
  };

  const onFileRemove = async (item) => {
    if (item.hasError) return;

    await props.deleteProlongFile(item.response);

    const newIncreaseLimitData = { ...increaseLimitData };

    newIncreaseLimitData.files = newIncreaseLimitData.files.filter((i) => i !== item.response);

    setIncreaseLimitData(newIncreaseLimitData);
  };

  const onValidToChange = (date, dateString) => {
    setIncreaseLimitData({ ...increaseLimitData, validTo: dateString });

    setShowValidationError(!validToIsValid(dateString));
  };

  const onExplanationChange = (e) => {
    setIncreaseLimitData({ ...increaseLimitData, explanation: e.target.value });
  };

  const validToIsValid = (value) => {
    return props.validTo ? dateIsLater(value) && dateIsLater(value, props.validTo) : false;
  };

  const beforeFilesUpload = (file, fileList) => {
    if (!fileSizeIsValid(file.size)) {
      file.validationText = t("fileSizeValidationMessage");
      file.hasError = true;
      setInvoiceFiles([...fileList, file]);
      return false;
    }

    return true;
  };

  const uploadFilesData = props.getProlongFileUploadData(props.productFid, props.contract?.id);

  return (
    <>
      <Modal
        width="48.125rem"
        className="increaseLimit contractFunction modal grey"
        title={t("prolongContract")}
        visible={props.modalVisible}
        okText={t("submit")}
        cancelText={t("cancel")}
        cancelButtonProps={{
          className: "button button--default button--large",
        }}
        okButtonProps={{
          className: "button button--primary button--large",
        }}
        onCancel={() => {
          props.onIncreaseLimitClose();
          setShowValidationError(false);
          setIncreaseLimitData(defaultIncreaseLimitData);
          setInvoiceFiles([]);
        }}
        onOk={async () => {
          if (validToIsValid(increaseLimitData.validTo)) {
            setLoading(true);

            const increaseLimitDataRequest = {
              contract_id: props.contract?.id,
              contract_prolong: {
                application_explanation: increaseLimitData.explanation,
                contract_requested_valid_to: increaseLimitData.validTo,
              },
              file_upload_uuids: increaseLimitData.files,
            };

            const response = await props.prolong(increaseLimitDataRequest);

            if (response.error) {
              setShowValidationError(true);
              setLoading(false);
              return;
            }

            props.onProlongSave();
            setIncreaseLimitData(defaultIncreaseLimitData);
            setInvoiceFiles([]);
            setLoading(false);
            setShowValidationError(false);
          } else {
            setShowValidationError(true);
          }
        }}
      >
        <Spin spinning={loading} tip={t("loading") + "..."}>
          <Row>
            <Col className="limits" span={11}>
              <label>{t("prolongContractValidTo")}</label>
              <p>{dateFormatter(props.validTo)}</p>
            </Col>
            <Col span={13}>
              <label>{requiredField(t("prolongUntil"))}</label>

              <DatePicker
                onChange={onValidToChange}
                className={`date-picker ${showValidationError && "ant-form-item-has-error"}`}
                placeholder={settings.dateFormat}
                value={increaseLimitData.validTo ? dateMomentFormatter(increaseLimitData.validTo) : ""}
              />
              {showValidationError && (
                <div className="ant-form-item-explain">
                  <div>{t("prolongValidationText")}</div>
                </div>
              )}
            </Col>
          </Row>
          <Row>
            <Col span={1}>1</Col>
            <Col span={23}>
              <p>{t("increaseLimitExplanationText")}</p>
              <label>{t("explanation")}</label>
              <TextArea
                placeholder={t("explanation")}
                autoSize
                onChange={onExplanationChange}
                value={increaseLimitData.explanation}
              />
            </Col>
          </Row>
          <Row>
            <Col span={1}>2</Col>

            <Col className="uploadSupportingDocuments" span={23}>
              <p>{t("uploadSupportingDocuments")}</p>
              <span>{t("uploadSupportingDocumentsDescription")}</span>
            </Col>
          </Row>

          <Row>
            <Col span={24}>
              <Spin spinning={fileUploadLoading} tip={t("loading") + "..."}>
                <Dragger
                  name="file"
                  multiple={true}
                  fileList={invoiceFiles}
                  beforeUpload={beforeFilesUpload}
                  action={uploadFilesData.action}
                  headers={uploadFilesData.headers}
                  onChange={onInvoiceFilesChange}
                  onRemove={onFileRemove}
                  showUploadList={{
                    showRemoveIcon: true,
                    removeIcon: <CloseOutlined />,
                  }}
                >
                  <p className="ant-upload-drag-icon"></p>
                  <p className="ant-upload-text">+ {t("uploadFiles")}</p>
                  <p className="ant-upload-hint">{t("or")}</p>
                  <p className="ant-upload-hint">{t("dragDropSupportingFilesDescription")}</p>
                </Dragger>
              </Spin>
            </Col>
          </Row>
        </Spin>
      </Modal>
    </>
  );
};

export default Prolong;
